import {svgFlagSources} from '@hconnect/common/assets/flags'
import {
  ControlledInput,
  Loading,
  clx,
  Tree,
  DropdownList,
  DropdownListItem,
  type TreeNode,
  type TreeState,
  type TreeEvent,
  type DropdownListItemProps
} from '@hm/fundamental'
import {type PlantHierarchyNode, PlantHierarchyType} from '@hproduce/suite-core'
import {TFunction} from 'i18next'
import {type SyntheticEvent, useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {
  flattenTreeNodes,
  filterFlatNodes,
  expandMatchingNodes,
  type FlatTreeNode
} from './treeUtils'

type PlantSelectTreeProps = {
  nodes?: PlantHierarchyNode[]
  isLoading: boolean
  onSelect: (hierarchyId: string) => void
  isOnDarkBackground?: boolean
}

/**
 * Transforms PlantHierarchyNode to fundamental TreeNode format.
 * Creates a searchTextMap for efficient lookup during filtering.
 */
const transformToTreeNode = (
  node: PlantHierarchyNode,
  t: TFunction<'translation', undefined, 'translation'>,
  searchTextMap: Map<string, string>
): TreeNode => {
  const translatedLabel = t(`suite.header.plantSelectTreeItems.${node.name}`, {
    defaultValue: node.name
  })
  searchTextMap.set(node.hierarchyId, node.name)

  return {
    id: node.hierarchyId,
    label: translatedLabel,
    left:
      node.type === PlantHierarchyType.Country && svgFlagSources[node.name]
        ? {type: 'icon', icon: svgFlagSources[node.name]}
        : undefined,
    children: node.children?.map((child) => transformToTreeNode(child, t, searchTextMap)) ?? []
  }
}

/**
 * Renders filtered results as a dropdown list.
 * Filtering is done in the parent component to avoid stale closure issues.
 */
const FilteredResultsList = ({
  filteredNodes,
  onSelect,
  activeId
}: {
  filteredNodes: FlatTreeNode[]
  onSelect: (id: string) => void
  activeId?: string
}) => {
  const {t} = useTranslation()
  const handleSelectItem = useCallback(
    (props: DropdownListItemProps) => {
      if (props.value) {
        onSelect(String(props.value))
      }
    },
    [onSelect]
  )

  if (filteredNodes.length === 0) {
    return (
      <div className="p-4 text-center text-foreground-secondary" data-test-id="no-results">
        {t('suite.header.plantSelectTree.noResults', {defaultValue: 'No results found'})}
      </div>
    )
  }

  return (
    <div
      className="max-h-[400px] overflow-y-auto"
      data-test-id="filtered-list"
      role="listbox"
      aria-label="Filtered plant results"
    >
      <DropdownList isOpen onSelectItem={handleSelectItem}>
        {filteredNodes.map((node) => (
          <DropdownListItem
            key={node.id}
            title={node.label}
            value={node.id}
            isSelected={node.id === activeId}
            data-cy={`filtered-item-${node.id}`}
          />
        ))}
      </DropdownList>
    </div>
  )
}

export const PlantSelectTree = ({
  nodes,
  isLoading,
  onSelect,
  isOnDarkBackground = false
}: PlantSelectTreeProps) => {
  const {t} = useTranslation()
  const [filter, setFilter] = useState('')
  const [activeId, setActiveId] = useState<string | undefined>()
  const [manualExpanded, setManualExpanded] = useState<Record<string, boolean>>({})

  // Transform hierarchy nodes to fundamental TreeNode format with searchTextMap
  // Only depends on nodes - translations will update on re-render
  const {treeNodes, searchTextMap, flatNodes} = useMemo(() => {
    if (!nodes) {
      return {treeNodes: [], searchTextMap: new Map<string, string>(), flatNodes: []}
    }
    const searchTextMap = new Map<string, string>()
    const treeNodes = nodes.map((node) => transformToTreeNode(node, t, searchTextMap))
    const flatNodes = flattenTreeNodes(treeNodes, [], searchTextMap)
    return {treeNodes, searchTextMap, flatNodes}
  }, [nodes, t])

  // Compute expanded state based on filter - memoized separately
  const filterExpanded = useMemo(() => {
    if (!filter) return null // null means use manualExpanded
    return expandMatchingNodes(treeNodes, filter, searchTextMap)
  }, [treeNodes, filter, searchTextMap])

  // Combine tree state - computed on each render, no useState needed for nodes/expanded
  const treeState: TreeState = useMemo(
    () => ({
      nodes: treeNodes,
      expanded: filterExpanded ?? manualExpanded,
      activeId
    }),
    [treeNodes, filterExpanded, manualExpanded, activeId]
  )

  const isFiltering = filter.trim().length > 0

  const handleTreeChange = useCallback(
    (newState: TreeState) => {
      // Only track manual expand/collapse changes when not filtering
      if (isFiltering) return
      setManualExpanded(newState.expanded)
    },
    [isFiltering]
  )

  const handleTreeEvent = useCallback(
    (event: TreeEvent) => {
      if (event.action === 'click') {
        setActiveId(event.nodeId)
        onSelect(event.nodeId)
      }
    },
    [onSelect]
  )

  const handleListSelect = useCallback(
    (nodeId: string) => {
      setActiveId(nodeId)
      onSelect(nodeId)
    },
    [onSelect]
  )

  const handleTreeLabelInteraction = useCallback((event: SyntheticEvent<HTMLDivElement>) => {
    const target = event.target
    if (!(target instanceof HTMLElement)) return

    const treeItem = target.closest<HTMLElement>('[role="treeitem"]')
    if (!treeItem) return

    const label = treeItem.querySelector<HTMLElement>('.truncate') ?? treeItem
    const fullText = treeItem.getAttribute('aria-label') ?? label.textContent ?? ''

    if (label.scrollWidth > label.clientWidth) {
      treeItem.title = fullText
    } else {
      treeItem.removeAttribute('title')
    }
  }, [])

  // Compute filtered nodes synchronously using useMemo
  const filteredNodes = useMemo(() => {
    const trimmedFilter = filter.trim()
    if (trimmedFilter.length > 0) {
      return filterFlatNodes(flatNodes, trimmedFilter)
    }
    return []
  }, [filter, flatNodes])

  if (isLoading) {
    return (
      <div
        className="w-full h-[300px] flex items-center justify-center"
        data-test-id="loading-spinner"
      >
        <Loading size="large" />
      </div>
    )
  }

  return (
    <div className="w-full h-full" data-test-id="plant_select_tree">
      <ControlledInput
        value={filter}
        className="m-4"
        name="plant_select_tree_filter"
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter"
        data-cy="plant_select_tree_filter"
      />
      {isFiltering ? (
        <FilteredResultsList
          key={filter}
          filteredNodes={filteredNodes}
          onSelect={handleListSelect}
          activeId={activeId}
        />
      ) : (
        <div onMouseOver={handleTreeLabelInteraction} onFocus={handleTreeLabelInteraction}>
          <Tree
            state={{...treeState, activeId}}
            className={clx('px-4 pb-4', isOnDarkBackground && 'text-foreground')}
            onChange={handleTreeChange}
            onEvent={handleTreeEvent}
            data-cy="plant-tree"
          />
        </div>
      )}
    </div>
  )
}
